import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";
import {
  Calendar as CalendarIcon,
  ForkKnife,
  ShoppingCart,
  Egg,
  Moon,
  Cookie,
  Copy as CopyIcon,
  Printer,
  ChatCircle,
  ArrowLeft,
} from "@phosphor-icons/react";

/* /plan/:token — read-only public view of a cook's shared meal plan.
 *
 * Purpose: partner / flat-mate / gran-visits-Sunday sees the whole week
 * without needing an account. Full recipes, aisle-grouped shopping list,
 * and a "Copy shopping list" clipboard button.
 *
 * No auth required. Backend `/api/public/meal-plan/{token}` returns 404
 * for missing / revoked / expired tokens and never leaks the owner's
 * user_id / email. Secret recipes are stripped.
 */

const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const MEAL_META = {
  breakfast: { label: "Breakfast", Icon: Egg, tone: "bg-amber-50" },
  lunch:     { label: "Lunch",     Icon: ForkKnife, tone: "bg-lime-50" },
  dinner:    { label: "Dinner",    Icon: Moon, tone: "bg-orange-50" },
  snacks:    { label: "Snacks",    Icon: Cookie, tone: "bg-rose-50" },
};
const AISLES = [
  { key: "produce", label: "Fresh produce", emoji: "🥬" },
  { key: "meat",    label: "Meat & fish",   emoji: "🥩" },
  { key: "dairy",   label: "Dairy & eggs",  emoji: "🥛" },
  { key: "bakery",  label: "Bakery",        emoji: "🥖" },
  { key: "frozen",  label: "Frozen",        emoji: "❄️" },
  { key: "baking",  label: "Baking",        emoji: "🧁" },
  { key: "spices",  label: "Spices & seasoning", emoji: "🌶" },
  { key: "pantry",  label: "Pantry",        emoji: "🥫" },
  { key: "drinks",  label: "Drinks",        emoji: "🍷" },
  { key: "other",   label: "Other",         emoji: "🛒" },
];

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function humanRange(mondayIso) {
  const start = new Date(mondayIso + "T00:00:00");
  const end = new Date(start); end.setDate(end.getDate() + 6);
  const opts = { day: "numeric", month: "short" };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
}
function addDays(iso, n) {
  // Compute in LOCAL time — never use toISOString() here, it silently
  // returns yesterday's UTC date when a user is in a tz ahead of UTC.
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function humanDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export default function SharedMealPlan() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSlot, setExpandedSlot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/meal-plan/${token}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "This meal plan link is no longer active");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const shoppingByAisle = useMemo(() => {
    const map = {};
    (data?.shopping_list || []).forEach((it) => {
      const a = it.aisle || "other";
      (map[a] = map[a] || []).push(it);
    });
    return map;
  }, [data]);

  const slotsByKey = useMemo(() => {
    const map = {};
    (data?.slots || []).forEach((s) => { map[`${s.day}:${s.meal_type}`] = s; });
    return map;
  }, [data]);

  const copyShoppingList = async () => {
    if (!data?.shopping_list?.length) {
      toast.info("No ingredients to copy — the cook hasn't picked any recipes yet.");
      return;
    }
    const lines = [`${data.cook_name}'s shopping list — ${humanRange(data.week_start)}`, "-".repeat(38), ""];
    AISLES.forEach(({ key, label, emoji }) => {
      const rows = shoppingByAisle[key] || [];
      if (!rows.length) return;
      lines.push(`${emoji} ${label}`);
      rows.forEach((r) => {
        const q = r.quantity ? `${r.quantity} ${r.unit || ""}`.trim() + " " : "";
        lines.push(`  [ ] ${q}${r.display_name}`);
      });
      lines.push("");
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n").trim());
      toast.success("Shopping list copied");
    } catch { toast.error("Copy failed"); }
  };

  const printPlan = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-rf-cream flex items-center justify-center px-4" data-testid="shared-plan-loading">
        <p className="text-rf-ink-soft">Loading the meal plan…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-rf-cream flex items-center justify-center px-4">
        <PageMeta title="Meal plan link" noindex />
        <div className="max-w-md text-center" data-testid="shared-plan-error">
          <CalendarIcon size={56} weight="duotone" className="mx-auto text-rf-orange mb-4" />
          <h1 className="font-display font-black text-3xl text-rf-ink mb-2">Link no longer active</h1>
          <p className="text-rf-ink-soft mb-6">
            {error || "This meal plan link has expired or been revoked. Ask the cook to send you a new one."}
          </p>
          <Link to="/" className="btn-orange inline-flex items-center gap-2">
            <ArrowLeft size={16} /> Recipe Filer home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rf-cream">
      <PageMeta
        title={`${data.cook_name}'s meal plan · ${humanRange(data.week_start)}`}
        description="A shared weekly meal plan and shopping list, sent from Recipe Filer."
        noindex
      />

      {/* Header */}
      <header className="bg-white border-b border-rf-ink/10 no-print">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 no-underline">
            <ForkKnife size={22} weight="duotone" className="text-rf-orange" />
            <span className="font-display font-black text-rf-ink">Recipe Filer</span>
          </Link>
          <div className="flex gap-2">
            <button
              onClick={copyShoppingList}
              className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"
              data-testid="shared-plan-copy-shopping"
            >
              <CopyIcon size={16} /> Copy shopping list
            </button>
            <button
              onClick={printPlan}
              className="btn-outline-ink !py-2 !px-3 text-sm inline-flex items-center gap-2"
              data-testid="shared-plan-print"
            >
              <Printer size={16} /> Print
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10" data-testid="shared-plan-body">
        {/* Personal header */}
        <div className="mb-8 flex items-center gap-4">
          {data.cook_picture ? (
            <img src={data.cook_picture} alt="" className="w-14 h-14 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-rf-amber/30 flex items-center justify-center shrink-0">
              <ForkKnife size={22} weight="duotone" className="text-rf-orange" />
            </div>
          )}
          <div>
            <p className="overline">{humanRange(data.week_start)}</p>
            <h1 className="font-display font-black text-3xl sm:text-4xl text-rf-ink leading-tight">
              {data.cook_name}&apos;s meal plan
            </h1>
          </div>
        </div>

        {/* Grid */}
        <div className="hidden md:block overflow-x-auto mb-10">
          <table className="w-full border-separate border-spacing-1" data-testid="shared-plan-grid">
            <thead>
              <tr>
                <th className="w-28"></th>
                {Object.keys(DAY_LABELS).map((k, i) => (
                  <th key={k} className="p-2 rounded-xl bg-rf-cream-alt/60">
                    <div className="font-display font-black text-rf-ink">{DAY_LABELS[k]}</div>
                    <div className="text-xs text-rf-ink-soft">{humanDate(addDays(data.week_start, i))}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(MEAL_META).map(([mKey, { label, Icon, tone }]) => (
                <tr key={mKey}>
                  <td className={`p-3 rounded-xl align-top ${tone}`}>
                    <div className="flex items-center gap-2 font-display font-black text-rf-ink">
                      <Icon size={18} weight="duotone" className="text-rf-orange" /> {label}
                    </div>
                  </td>
                  {Object.keys(DAY_LABELS).map((d) => {
                    const slot = slotsByKey[`${d}:${mKey}`];
                    return (
                      <td key={d} className="p-1 align-top">
                        <SharedSlotCell
                          slot={slot}
                          onClick={() => slot?.recipe?.ingredients?.length && setExpandedSlot(slot)}
                          testId={`shared-slot-${d}-${mKey}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stack */}
        <div className="md:hidden space-y-4 mb-10">
          {Object.keys(DAY_LABELS).map((dKey, i) => (
            <div key={dKey} className="rounded-2xl p-3 border border-rf-ink/10 bg-white">
              <h3 className="font-display font-black text-rf-ink text-lg mb-2">
                {DAY_LABELS[dKey]} <span className="text-rf-ink-soft text-sm font-normal">{humanDate(addDays(data.week_start, i))}</span>
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(MEAL_META).map(([mKey, { label, Icon }]) => {
                  const slot = slotsByKey[`${dKey}:${mKey}`];
                  return (
                    <div key={mKey}>
                      <div className="text-xs text-rf-ink-soft mb-1 inline-flex items-center gap-1">
                        <Icon size={12} weight="duotone" className="text-rf-orange" /> {label}
                      </div>
                      <SharedSlotCell
                        slot={slot}
                        onClick={() => slot?.recipe?.ingredients?.length && setExpandedSlot(slot)}
                        testId={`shared-slot-${dKey}-${mKey}`}
                        compact
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Shopping list */}
        {data.shopping_list?.length > 0 && (
          <section className="card-recipe !p-6" data-testid="shared-shopping-list">
            <div className="flex items-center gap-3 mb-4">
              <ShoppingCart size={22} weight="duotone" className="text-rf-orange" />
              <h2 className="font-display font-black text-rf-ink text-2xl">Shopping list</h2>
              <span className="chip !bg-rf-amber/25 !text-rf-ink !border-0 text-xs ml-auto">
                {data.shopping_list.length} items
              </span>
            </div>
            {AISLES.map(({ key, label, emoji }) => {
              const rows = shoppingByAisle[key] || [];
              if (!rows.length) return null;
              return (
                <div key={key} className="mb-4 last:mb-0">
                  <h3 className="font-display font-black text-rf-ink text-base mb-2">
                    <span aria-hidden="true">{emoji}</span> {label}
                  </h3>
                  <ul className="space-y-0.5">
                    {rows.map((r, idx) => {
                      const q = r.quantity ? `${r.quantity} ${r.unit || ""}`.trim() : "";
                      return (
                        <li key={`${key}-${idx}`} className="flex items-start gap-2 py-1 border-b border-rf-ink/5 last:border-0">
                          <span className="w-4 h-4 rounded border-2 border-rf-ink/25 mt-1 shrink-0" aria-hidden="true" />
                          <p className="text-rf-ink text-sm">
                            {q && <span className="font-bold">{q} </span>}
                            {r.display_name}
                            {r.sources?.length ? <span className="text-xs text-rf-ink-soft"> · for {r.sources.join(" + ")}</span> : null}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </section>
        )}

        {/* Footer CTA */}
        <div className="mt-12 text-center no-print">
          <p className="text-rf-ink-soft mb-3">Want your own weekly plan and shopping list?</p>
          <Link to="/register" className="btn-orange inline-flex items-center gap-2" data-testid="shared-plan-cta">
            Try Recipe Filer free →
          </Link>
        </div>
      </main>

      {/* Recipe detail overlay */}
      {expandedSlot && (
        <SharedRecipeSheet slot={expandedSlot} onClose={() => setExpandedSlot(null)} />
      )}
    </div>
  );
}

function SharedSlotCell({ slot, onClick, testId, compact = false }) {
  if (!slot || (!slot.recipe_id && !slot.recipe_title && !slot.note)) {
    return (
      <div
        data-testid={testId}
        className={`w-full ${compact ? "h-14" : "h-20"} rounded-xl border-2 border-dashed border-rf-ink/10 flex items-center justify-center text-rf-ink-soft/60 text-xs`}
      >
        —
      </div>
    );
  }
  const hasRecipeDetails = !!(slot.recipe?.ingredients?.length);
  return (
    <button
      onClick={hasRecipeDetails ? onClick : undefined}
      disabled={!hasRecipeDetails}
      data-testid={testId}
      className={`w-full ${compact ? "h-14" : "h-20"} rounded-xl bg-white border border-rf-ink/10 text-left flex overflow-hidden ${hasRecipeDetails ? "hover:border-rf-orange hover:shadow cursor-pointer" : "cursor-default"}`}
    >
      {slot.recipe_image ? (
        <img src={slot.recipe_image} alt="" className={`${compact ? "w-12" : "w-16"} h-full object-cover shrink-0`} />
      ) : slot.recipe_id ? (
        <div className={`${compact ? "w-12" : "w-16"} h-full shrink-0 bg-rf-amber/25 flex items-center justify-center`}>
          <ForkKnife size={compact ? 14 : 18} weight="duotone" className="text-rf-orange" />
        </div>
      ) : null}
      <div className="flex-1 min-w-0 p-2 flex flex-col">
        <p className={`font-display font-bold text-rf-ink line-clamp-2 ${compact ? "text-xs" : "text-sm"}`}>
          {slot.recipe_title || "Note"}
        </p>
        {slot.note && (
          <p className="text-[10px] text-rf-ink-soft italic line-clamp-1 mt-0.5">{slot.note}</p>
        )}
      </div>
    </button>
  );
}

function SharedRecipeSheet({ slot, onClose }) {
  const r = slot.recipe;
  if (!r) return null;
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-rf-ink/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="shared-recipe-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-2xl bg-rf-cream rounded-t-3xl sm:rounded-3xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl"
      >
        <div className="flex items-start justify-between p-5 border-b border-rf-ink/10 bg-white">
          <div>
            <p className="overline">
              {DAY_LABELS[slot.day]} · {MEAL_META[slot.meal_type]?.label} · Serves {slot.servings || r.serves}
            </p>
            <h2 className="font-display font-black text-rf-ink text-xl">{r.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-rf-ink/10" aria-label="Close">
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {r.image && (
            <img src={r.image} alt="" className="w-full max-h-56 object-cover rounded-2xl" />
          )}
          {slot.note && (
            <p className="text-sm text-rf-ink italic bg-rf-amber/15 border border-rf-amber/30 rounded-xl p-3 inline-flex items-start gap-2">
              <ChatCircle size={16} weight="duotone" className="text-rf-orange shrink-0 mt-0.5" />
              {slot.note}
            </p>
          )}
          <section>
            <h3 className="overline mb-2">Ingredients</h3>
            <ul className="space-y-1">
              {r.ingredients.map((ing, i) => {
                const qty = ing.quantity ? `${ing.quantity} ${ing.unit || ""}`.trim() : "";
                return (
                  <li key={i} className="text-rf-ink text-sm border-b border-rf-ink/5 last:border-0 py-1">
                    {qty && <span className="font-bold">{qty} </span>}{ing.name}
                    {ing.notes ? <span className="text-rf-ink-soft"> — {ing.notes}</span> : null}
                  </li>
                );
              })}
            </ul>
          </section>
          {r.instructions?.length > 0 && (
            <section>
              <h3 className="overline mb-2">Instructions</h3>
              <ol className="list-decimal list-inside space-y-2">
                {r.instructions.map((step, i) => (
                  <li key={i} className="text-rf-ink text-sm leading-relaxed">{step}</li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
